import * as React from 'react';
import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {RouteComponentProps, Link} from 'react-router-dom';
import {ConfirmProps, withConfirm} from '@deviceinsight/ng-ui-basic-components';
import {ContentHeader, ReactableColumnDef, Reactable} from '@deviceinsight/ng-ui-components';
import {AssetGroup, SortDirection, User} from '@deviceinsight/ng-ui-api-client';
import {updateRule} from '../../api/ruleApi';
import {
	AUTHORITY_CREATE_RULE,
	AUTHORITY_DELETE_RULE,
	AUTHORITY_UPDATE_RULE
} from '../../modules/ruleengine/config/consts';
import type {Rule} from '../../modules/ruleengine/types';
import columnDefinitions from '../../modules/ruleengine/config/columnDefinitions';
import useDeleteRule from './hooks/useDeleteRule';
import useSearchRules from './hooks/useSearchRules';
import {
	buildEmptyContent,
	initSearchSettings,
	getQueryParamsFromSearchSettings,
	debouncedNameFilter,
	HEADER_TITLE
} from './ruleListeHelpers';
import TriggerDetails from './trigger/TriggerDetails';
type OwnProps = {
	assetGroup: AssetGroup;
	currentUser: User;
};
export type Props = ConfirmProps & OwnProps & RouteComponentProps;

const RuleList = ({confirm, assetGroup: {id: assetGroupId}, currentUser, location, history}: Props) => {
	const {t} = useTranslation();
	const {useState, useEffect} = React;
	const [searchSettings, setSearchSettings] = useState(() => initSearchSettings(location.search));
	const [isLoading, setIsLoading] = useState(true);
	const [ruleToDelete, setRuleToDelete] = useState<Rule | undefined | null>(undefined);
	const [ruleToUpdate, setRuleToUpdate] = useState<Rule | undefined | null>(undefined);
	const [ruleToShowTriggerDetails, setRuleToShowTriggerDetails] = useState<Rule | null | undefined>();
	const [columnDefs, setColumnDefs] = useState<ReactableColumnDef<Rule>[]>([]);
	const [ruleRights, setRuleRights] = useState({
		create: false,
		edit: false,
		deleteRule: false
	});
	const [filteredName, setFilteredName] = useState(searchSettings.nameFilter);
	const [rules, setRules] = useState(() => buildEmptyContent());
	useSearchRules(setIsLoading, searchSettings, assetGroupId, setRules, setRuleToDelete, setRuleToUpdate);
	useEffect(() => {
		if (filteredName !== undefined && filteredName !== searchSettings.nameFilter) {
			debouncedNameFilter(filteredName as string, searchSettings, setSearchSettings);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredName]);
	useEffect(() => {
		const deleteRule = !!currentUser.authorities.find((authority: string) => authority === AUTHORITY_DELETE_RULE);
		const edit = !!currentUser.authorities.find((authority: string) => authority === AUTHORITY_UPDATE_RULE);
		const create = !!currentUser.authorities.find((authority: string) => authority === AUTHORITY_CREATE_RULE);
		setRuleRights({
			create,
			edit,
			deleteRule
		});
	}, [currentUser]);
	useEffect(() => {
		const {edit, deleteRule} = ruleRights;
		const columnDefs = columnDefinitions(
			setRuleToDelete,
			setRuleToUpdate,
			setRuleToShowTriggerDetails,
			deleteRule,
			edit,
			assetGroupId,
			filteredName || undefined
		);
		setColumnDefs(columnDefs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ruleRights]);
	useDeleteRule(ruleToDelete, setRuleToDelete, searchSettings, setSearchSettings, confirm, rules);
	useEffect(() => {
		if (ruleToUpdate) {
			updateRule(ruleToUpdate).then(() => setSearchSettings({...searchSettings}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ruleToUpdate]);
	useEffect(() => {
		history.push({
			search: getQueryParamsFromSearchSettings(searchSettings)
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchSettings]);
	const {paginationSettings} = searchSettings;

	const createTitle = t('ruleengine.list.create', {
		defaultValue: 'Create rule'
	});

	return (
		<div id="rule-list-table">
			{ruleRights.create && (
				<ContentHeader title={HEADER_TITLE}>
					<div className="di header-toolbar">
						<Link
							className="tool add"
							to={`/assetgroups/${assetGroupId}/ruleengine/create`}
							title={createTitle}
						/>
					</div>
				</ContentHeader>
			)}
			<Reactable
				columnDefs={columnDefs}
				uniqueItemKey={(item) => item.id}
				paginationSettings={paginationSettings}
				onChangePage={(page) =>
					setSearchSettings({...searchSettings, paginationSettings: {...paginationSettings, page}})
				}
				onSort={(property: string, direction: SortDirection) =>
					setSearchSettings({
						...searchSettings,
						paginationSettings: {
							...paginationSettings,
							sort: {
								property,
								direction
							}
						}
					})
				}
				onFilter={({name}) => {
					name !== filteredName && setFilteredName(name);
				}}
				pageable={rules}
				isLoading={isLoading}
			/>
			{ruleToShowTriggerDetails && (
				<TriggerDetails
					rule={ruleToShowTriggerDetails}
					assetGroupId={ruleToShowTriggerDetails.assetGroupId}
					onClose={() => setRuleToShowTriggerDetails(undefined)}
				/>
			)}
		</div>
	);
};
export default withConfirm(RuleList) as React.ComponentType<OwnProps>;
